import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { required, nonEmptyArray } from '../../util/validators';
import {
  FieldTextInput,
  FieldSelect,
  FieldBoolean,
  FieldCheckboxGroup,
  FieldRadioButton,
} from '../../components';
import { getFieldsForCategories } from '../../util/wedlyListingConfigHelpers';

import css from './CategoryListingFields.module.css';

const toCheckboxOptions = options => options.map(o => ({ key: o.value, label: o.label }));

const FieldRadioGroup = props => {
  const { name, id, label, options } = props;

  return (
    <fieldset className={css.fieldset}>
          <legend className={css.legend}>{label}</legend>
          <div className={css.radioGroup} role="radiogroup" aria-label={label}>
            {options.map(option => (
              <FieldRadioButton
                key={option.value}
                id={`${id}_${option.value}`}
                name={name}
                value={option.value}
                label={option.label}
              />
            ))}
          </div>
    </fieldset>
  );
};

const WedlyField = props => {
  const { field, formId, defaultRequiredMessage } = props;
  const intl = useIntl();
  const name = field.key;
  const id = formId ? `${formId}.${name}` : name;
  const label = field.label;
  const validateMaybe = field.required
    ? {
        validate:
          field.type === 'chips' || field.type === 'multiSelect'
            ? nonEmptyArray(field.requiredMessage || defaultRequiredMessage)
            : required(field.requiredMessage || defaultRequiredMessage),
      }
    : {};

  switch (field.type) {
    case 'radio':
      return (
        <FieldRadioGroup
          name={name}
          id={id}
          label={label}
          options={field.options || []}
          {...validateMaybe}
        />
      );
    case 'select':
      return (
        <FieldSelect className={css.field} name={name} id={id} label={label} {...validateMaybe}>
          <option disabled value="">
            {field.placeholder ||
              intl.formatMessage({ id: 'CategoryListingFields.selectPlaceholder' })}
          </option>
          {(field.options || []).map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </FieldSelect>
      );
    case 'multiSelect':
    case 'chips':
      return (
        <FieldCheckboxGroup
          className={classNames(css.field, css.chipGroup)}
          id={id}
          name={name}
          label={label}
          options={toCheckboxOptions(field.options || [])}
          {...validateMaybe}
        />
      );
    case 'toggle':
      return (
        <FieldBoolean
          className={css.field}
          id={id}
          name={name}
          label={label}
          placeholder={intl.formatMessage({ id: 'CategoryListingFields.toggleNo' })}
          {...validateMaybe}
        />
      );
    case 'number':
      return (
        <FieldTextInput
          className={css.field}
          id={id}
          name={name}
          type="number"
          step="1"
          label={label}
          placeholder={field.placeholder}
          parse={value => {
            const parsed = Number.parseInt(value, 10);
            return Number.isNaN(parsed) ? null : parsed;
          }}
          {...validateMaybe}
        />
      );
    case 'textarea':
      return (
        <FieldTextInput
          className={css.field}
          id={id}
          name={name}
          type="textarea"
          label={label}
          placeholder={field.placeholder}
          {...validateMaybe}
        />
      );
    case 'text':
    default:
      return (
        <FieldTextInput
          className={css.field}
          id={id}
          name={name}
          type="text"
          label={label}
          placeholder={field.placeholder}
          {...validateMaybe}
        />
      );
  }
};

/**
 * Renders category-specific listing fields from Wedly configuration.
 */
const CategoryListingFields = props => {
  const intl = useIntl();
  const { categoryIds = [], listingCategories = [], formId, allCategoriesChosen } = props;

  if (!allCategoriesChosen || !listingCategories?.length) {
    return null;
  }

  const fields = getFieldsForCategories(categoryIds, listingCategories);
  if (fields.length === 0) {
    return null;
  }

  const defaultRequiredMessage = intl.formatMessage({
    id: 'CategoryListingFields.required',
  });

  return (
    <div className={css.root}>
      <p className={css.sectionIntro}>
        {intl.formatMessage({ id: 'CategoryListingFields.sectionIntro' })}
      </p>
      {fields.map(field => (
        <WedlyField
          key={field.key}
          field={field}
          formId={formId}
          defaultRequiredMessage={defaultRequiredMessage}
        />
      ))}
    </div>
  );
};

export default CategoryListingFields;
